import React, { useEffect, useMemo, useState } from "react";
import Header from "./HostelHeader";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import Nav from "../master/NavBar";
import { DashboardData } from "../../api/HostelService";
import WelcomeBar from "./WelcomeBar";
import usePrivateLink from "../config/usePrivateLink";

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [Data, setData] = useState({});
  const [hostel_id, setHostelId] = useState(localStorage.getItem("hostel_id"));
  const hasAccess = usePrivateLink();
  /* =========================================================
     HOSTEL CHANGE
  ========================================================= */

  useEffect(() => {
    const handleStorageChange = () => {
      setHostelId(localStorage.getItem("hostel_id"));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  /* =========================================================
     GET DASHBOARD DATA
  ========================================================= */

  const getData = async () => {
    setLoading(true);

    let addUrl = "";

    if (hostel_id) {
      addUrl = `&hostel_id=${hostel_id}`;
    }

    try {
      const response = await DashboardData(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Something went wrong!",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [hostel_id]);

  /* =========================================================
     DATA
  ========================================================= */

  const totalStudents = Number(Data?.total_students || 0);
  const activeStudents = Number(Data?.active_students || 0);
  const inactiveStudents = Number(Data?.inactive_students || 0);

  const totalRooms = Number(Data?.total_rooms || 0);
  const totalBeds = Number(Data?.total_beds || 0);

  const todayCollection = Number(Data?.today_collection || 0);

  const weekCollection = Number(Data?.week_data?.collection || 0);
  const weekExpenses = Number(Data?.week_data?.expenses || 0);
  const weekValue = Number(Data?.week_data?.value || 0);

  const weekStatus = String(Data?.week_data?.status || "").toLowerCase();

  const financeDays = Array.isArray(Data?.finance_7days?.days)
    ? Data.finance_7days.days
    : [];

  const financeCollection = Array.isArray(Data?.finance_7days?.collection)
    ? Data.finance_7days.collection
    : [];

  const financeExpenses = Array.isArray(Data?.finance_7days?.expenses)
    ? Data.finance_7days.expenses
    : [];

  const hasFinanceData =
    financeCollection.some((value) => Number(value) > 0) ||
    financeExpenses.some((value) => Number(value) > 0);

  /* =========================================================
     CALCULATIONS
  ========================================================= */

  const occupancyPercentage =
    totalBeds > 0
      ? Math.min(100, Math.round((activeStudents / totalBeds) * 100))
      : 0;

  const availableBeds = Math.max(totalBeds - activeStudents, 0);

  const collectionPercentage =
    weekCollection + weekExpenses > 0
      ? Math.round((weekCollection / (weekCollection + weekExpenses)) * 100)
      : 0;

  const activeStudentPercentage =
    totalStudents > 0 ? Math.round((activeStudents / totalStudents) * 100) : 0;

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  /* =========================================================
     CHART DATA
  ========================================================= */

  const chartData = useMemo(() => {
    return financeDays.map((day, index) => ({
      day,
      collection: Number(financeCollection[index] || 0),
      expense: Number(financeExpenses[index] || 0),
    }));
  }, [financeDays, financeCollection, financeExpenses]);

  const maxChartValue = Math.max(
    ...chartData.flatMap((item) => [item.collection, item.expense]),
    1,
  );

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <WelcomeBar />

                {/* =====================================================
                    HEADER
                ===================================================== */}

                <div className="dashboard-topbar">
                  <div>
                    <div className="dashboard-eyebrow">HOSTEL MANAGEMENT</div>

                    <h4 className="dashboard-title">Dashboard Overview</h4>

                    <p className="dashboard-subtitle">
                      A quick view of your hostel's students, occupancy and
                      financial activity.
                    </p>
                  </div>

                  <div className="dashboard-status">
                    <span className="status-dot"></span>
                    <span>Live Overview</span>
                  </div>
                </div>

                {/* =====================================================
                    KPI CARDS
                ===================================================== */}

                <div className="row g-3 mb-4">
                  {/* TOTAL STUDENTS */}
                  {hasAccess("/hostel/dashboard/students") && (
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card stat-card stat-card-primary">
                        <div className="stat-card-top">
                          <div className="stat-icon">
                            <i className="ri-group-line"></i>
                          </div>

                          <span className="stat-icon-arrow">
                            <i className="ri-arrow-right-up-line"></i>
                          </span>
                        </div>

                        <div className="stat-label">Total Students</div>

                        <div className="stat-value">
                          {totalStudents.toLocaleString("en-IN")}
                        </div>

                        <div className="stat-footer">
                          <span>
                            <i className="ri-user-follow-line"></i>
                            {activeStudents} active
                          </span>

                          <span>{inactiveStudents} inactive</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TOTAL ROOMS */}
                  {hasAccess("/hostel/dashboard/rooms") && (
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card stat-card stat-card-warning">
                        <div className="stat-card-top">
                          <div className="stat-icon">
                            <i className="ri-building-4-line"></i>
                          </div>

                          <span className="stat-icon-arrow">
                            <i className="ri-arrow-right-up-line"></i>
                          </span>
                        </div>

                        <div className="stat-label">Total Rooms</div>

                        <div className="stat-value">
                          {totalRooms.toLocaleString("en-IN")}
                        </div>

                        <div className="stat-footer">
                          <span>
                            <i className="ri-hotel-bed-line"></i>
                            {totalBeds} total beds
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* BED OCCUPANCY */}
                  {hasAccess("/hostel/dashboard/occupancy") && (
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card stat-card stat-card-success">
                        <div className="stat-card-top">
                          <div className="stat-icon">
                            <i className="ri-pie-chart-2-line"></i>
                          </div>

                          <span className="stat-percentage">
                            {occupancyPercentage}%
                          </span>
                        </div>

                        <div className="stat-label">Bed Occupancy</div>

                        <div className="stat-value">
                          {activeStudents}
                          <small> / {totalBeds}</small>
                        </div>

                        <div className="stat-progress">
                          <span
                            style={{
                              width: `${occupancyPercentage}%`,
                            }}
                          />
                        </div>

                        <div className="stat-footer">
                          <span>{availableBeds} beds available</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TODAY COLLECTION */}
                  {hasAccess("/hostel/dashboard/collection") && (
                    <div className="col-12 col-sm-6 col-xl-3">
                      <div className="card stat-card stat-card-info">
                        <div className="stat-card-top">
                          <div className="stat-icon">
                            <i className="ri-wallet-3-line"></i>
                          </div>

                          <span className="stat-icon-arrow">
                            <i className="ri-arrow-right-up-line"></i>
                          </span>
                        </div>

                        <div className="stat-label">Today's Collection</div>

                        <div className="stat-value stat-value-currency">
                          {formatCurrency(todayCollection)}
                        </div>

                        <div className="stat-footer">
                          <span>
                            <i className="ri-calendar-check-line"></i>
                            Today's receipts
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* =====================================================
                    FINANCIAL OVERVIEW
                ===================================================== */}

                <div className="row g-3 mb-4">
                  {/* WEEKLY SUMMARY */}
                  {hasAccess("/hostel/dashboard/financial/summary") && (
                    <div className="col-12 col-xl-4">
                      <div className="card dashboard-card financial-summary-card h-100">
                        <div className="card-heading">
                          <div>
                            <h6>Weekly Financial Summary</h6>

                            <p>Last 7 days</p>
                          </div>

                          <div className="heading-icon">
                            <i className="ri-funds-line"></i>
                          </div>
                        </div>

                        <div className="financial-result">
                          <span className="result-label">Net Result</span>

                          <div
                            className={`result-value ${weekStatus === "profit"
                              ? "text-success"
                              : weekStatus === "loss"
                                ? "text-danger"
                                : ""
                              }`}
                          >
                            {formatCurrency(Math.abs(weekValue))}
                          </div>

                          {weekValue !== 0 ? (
                            <span
                              className={`result-status ${weekStatus === "profit"
                                ? "result-profit"
                                : "result-loss"
                                }`}
                            >
                              <i
                                className={
                                  weekStatus === "profit"
                                    ? "ri-arrow-up-line"
                                    : "ri-arrow-down-line"
                                }
                              />

                              {weekStatus === "profit" ? "Profit" : "Loss"}
                            </span>
                          ) : (
                            <span className="result-status result-neutral">
                              <i className="ri-information-line" />
                              No activity
                            </span>
                          )}
                        </div>

                        <div className="finance-breakdown">
                          <div className="card finance-item">
                            <div className="finance-item-icon collection">
                              <i className="ri-arrow-down-circle-line"></i>
                            </div>

                            <div>
                              <span>Collection</span>
                              <strong>{formatCurrency(weekCollection)}</strong>
                            </div>
                          </div>

                          <div className="card finance-item">
                            <div className="finance-item-icon expense">
                              <i className="ri-arrow-up-circle-line"></i>
                            </div>

                            <div>
                              <span>Expense</span>
                              <strong>{formatCurrency(weekExpenses)}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="collection-ratio">
                          <div className="ratio-header">
                            <span>Collection ratio</span>

                            <strong>{collectionPercentage}%</strong>
                          </div>

                          <div className="ratio-track">
                            <span
                              style={{
                                width: `${collectionPercentage}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* DONUT */}
                  {hasAccess("/hostel/dashboard/collection/expense") && (
                    <div className="col-12 col-xl-3">
                      <div className="card dashboard-card h-100">
                        <div className="card-heading">
                          <div>
                            <h6>Collection vs Expense</h6>

                            <p>Weekly distribution</p>
                          </div>
                        </div>

                        {weekCollection > 0 || weekExpenses > 0 ? (
                          <div className="donut-section">
                            <div
                              className="donut-chart"
                              style={{
                                background: `conic-gradient(
                                var(--bs-success) 0 ${collectionPercentage}%,
                                var(--bs-danger) ${collectionPercentage}% 100%
                              )`,
                              }}
                            >
                              <div className="donut-inner">
                                <strong>{collectionPercentage}%</strong>

                                <span>Collection</span>
                              </div>
                            </div>

                            <div className="donut-legend">
                              <div>
                                <span className="legend-dot collection-dot" />

                                <span>Collection</span>

                                <strong>{formatCurrency(weekCollection)}</strong>
                              </div>

                              <div>
                                <span className="legend-dot expense-dot" />

                                <span>Expense</span>

                                <strong>{formatCurrency(weekExpenses)}</strong>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <EmptyState
                            icon="ri-pie-chart-line"
                            title="No financial data"
                            text="No collection or expense activity."
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* 7 DAY CHART */}
                  {hasAccess("/hostel/dashboard/day/finance/activity") && (
                    <div className="col-12 col-xl-5">
                      <div className="card dashboard-card h-100">
                        <div className="card-heading">
                          <div>
                            <h6>7-Day Finance Activity</h6>

                            <p>Collection and expense trend</p>
                          </div>

                          <div className="chart-legend">
                            <span>
                              <i className="legend-dot collection-dot" />
                              Collection
                            </span>

                            <span>
                              <i className="legend-dot expense-dot" />
                              Expense
                            </span>
                          </div>
                        </div>

                        {hasFinanceData ? (
                          <div className="bar-chart">
                            {chartData.map((item, index) => {
                              const collectionHeight =
                                (item.collection / maxChartValue) * 100;

                              const expenseHeight =
                                (item.expense / maxChartValue) * 100;

                              return (
                                <div
                                  className="chart-column"
                                  key={`${item.day}-${index}`}
                                >
                                  <div className="chart-bars">
                                    <div
                                      className="chart-bar collection-bar"
                                      style={{
                                        height: `${Math.max(
                                          collectionHeight,
                                          item.collection > 0 ? 4 : 1,
                                        )}%`,
                                      }}
                                      title={`Collection: ${formatCurrency(
                                        item.collection,
                                      )}`}
                                    />

                                    <div
                                      className="chart-bar expense-bar"
                                      style={{
                                        height: `${Math.max(
                                          expenseHeight,
                                          item.expense > 0 ? 4 : 1,
                                        )}%`,
                                      }}
                                      title={`Expense: ${formatCurrency(
                                        item.expense,
                                      )}`}
                                    />
                                  </div>

                                  <span className="chart-day">
                                    {String(item.day).substring(0, 3)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <EmptyState
                            icon="ri-bar-chart-box-line"
                            title="No chart data"
                            text="No activity recorded during the last 7 days."
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* =====================================================
                    OCCUPANCY + STUDENT SUMMARY
                ===================================================== */}

                <div className="row g-3 mb-4">
                  {/* OCCUPANCY */}
                  {hasAccess("/hostel/dashboard/occupancy") && (
                    <div className="col-12 col-lg-6">
                      <div className="card dashboard-card occupancy-card">
                        <div className="card-heading">
                          <div>
                            <h6>Hostel Occupancy</h6>

                            <p>Current bed allocation</p>
                          </div>

                          <div className="heading-icon">
                            <i className="ri-hotel-bed-line"></i>
                          </div>
                        </div>

                        <div className="occupancy-layout">
                          <div className="occupancy-circle">
                            <svg viewBox="0 0 120 120" className="occupancy-svg">
                              <circle
                                cx="60"
                                cy="60"
                                r="48"
                                className="occupancy-track"
                              />

                              <circle
                                cx="60"
                                cy="60"
                                r="48"
                                className="occupancy-progress"
                                strokeDasharray={`${occupancyPercentage * 3.02} 302`}
                              />
                            </svg>

                            <div className="occupancy-circle-content">
                              <strong>{occupancyPercentage}%</strong>

                              <span>Occupied</span>
                            </div>
                          </div>

                          <div className="occupancy-details">
                            <div className="occupancy-detail">
                              <span>
                                <i className="ri-user-line"></i>
                                Occupied
                              </span>

                              <strong>{activeStudents}</strong>
                            </div>

                            <div className="occupancy-detail">
                              <span>
                                <i className="ri-hotel-bed-line"></i>
                                Available
                              </span>

                              <strong>{availableBeds}</strong>
                            </div>

                            <div className="occupancy-detail">
                              <span>
                                <i className="ri-home-4-line"></i>
                                Rooms
                              </span>

                              <strong>{totalRooms}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STUDENT OVERVIEW */}
                  {hasAccess("/hostel/dashboard/students") && (
                    <div className="col-12 col-lg-6">
                      <div className="card dashboard-card">
                        <div className="card-heading">
                          <div>
                            <h6>Student Overview</h6>

                            <p>Current student status</p>
                          </div>

                          <div className="heading-icon">
                            <i className="ri-team-line"></i>
                          </div>
                        </div>

                        <div className="student-overview">
                          <div className="student-overview-total">
                            <span>Total Students</span>

                            <strong>
                              {totalStudents.toLocaleString("en-IN")}
                            </strong>
                          </div>

                          <div className="student-status-row">
                            <div className="student-status">
                              <div className="student-status-icon active">
                                <i className="ri-user-follow-line"></i>
                              </div>

                              <div>
                                <span>Active Students</span>

                                <strong>{activeStudents}</strong>
                              </div>
                            </div>

                            <div className="student-status">
                              <div className="student-status-icon inactive">
                                <i className="ri-user-unfollow-line"></i>
                              </div>

                              <div>
                                <span>Inactive Students</span>

                                <strong>{inactiveStudents}</strong>
                              </div>
                            </div>
                          </div>

                          <div className="student-progress">
                            <div className="student-progress-bar">
                              <span
                                style={{
                                  width: `${activeStudentPercentage}%`,
                                }}
                              />
                            </div>

                            <div className="student-progress-label">
                              <span>{activeStudentPercentage}% active</span>

                              <span>{inactiveStudents} inactive</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* =====================================================
                    CALENDAR
                ===================================================== */}

                {/* <div className="dashboard-card calendar-card mb-4">
                  <div className="card-heading">
                    <div>
                      <h6>Hostel Calendar</h6>

                      <p>Monthly activity overview</p>
                    </div>

                    <div className="heading-icon">
                      <i className="ri-calendar-event-line"></i>
                    </div>
                  </div>

                  <div className="calendar-wrapper">
                    <FullCalendar
                      plugins={[dayGridPlugin]}
                      initialView="dayGridMonth"
                      height="auto"
                      contentHeight="auto"
                      expandRows={true}
                      fixedWeekCount={false}
                      dayMaxEvents={2}
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "",
                      }}
                      buttonText={{
                        today: "Today",
                      }}
                      events={Data?.calendar_events || []}
                    />
                  </div>
                </div> */}

                {/* <div className="content-backdrop fade" /> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===============================================================
          STYLES
      =============================================================== */}

      <style>{`

        /* =========================================================
           THEME VARIABLES
        ========================================================= */

        /*
          IMPORTANT:
          Never use #fff for dashboard cards.

          --bs-card-bg is used when your theme provides it.
          Otherwise --bs-body-bg is used.

          Therefore:
          Light mode -> light card
          Dark mode  -> dark card
        */

        .dashboard-topbar,
.stat-card,
.dashboard-card {
  background-color: var(--bs-card-bg) !important;
}


        /* =========================================================
           TOP HEADER
        ========================================================= */

        .dashboard-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 20px;

          margin-bottom: 22px;
          padding: 22px 24px;

          border: 1px solid var(--bs-border-color);
          border-radius: 16px;

          

          box-shadow:
            0 3px 12px rgba(0, 0, 0, 0.06);
        }

        .dashboard-eyebrow {
          margin-bottom: 5px;

          color: var(--bs-primary);

          font-size: 10px;
          font-weight: 800;

          letter-spacing: 1.2px;
        }

        .dashboard-title {
          margin: 0 0 5px;

          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 1.35rem;
          font-weight: 750;
        }

        .dashboard-subtitle {
          margin: 0;

          color: var(--bs-secondary-color);

          font-size: 0.78rem;
        }

        .dashboard-status {
          display: inline-flex;
          align-items: center;

          gap: 8px;

          flex-shrink: 0;

          padding: 8px 12px;

          border: 1px solid var(--bs-border-color);
          border-radius: 50px;

          color: var(--bs-secondary-color);

          

          font-size: 0.72rem;
          font-weight: 650;
        }

        .status-dot {
          width: 7px;
          height: 7px;

          border-radius: 50%;

          background: var(--bs-success);

          box-shadow:
            0 0 0 4px
            rgba(var(--bs-success-rgb), 0.12);
        }


        /* =========================================================
           KPI CARDS
        ========================================================= */

        .stat-card {
  position: relative;

  min-height: 172px;
  padding: 18px;

  border: 1px solid var(--bs-border-color);
  border-radius: 16px;

  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.055);

  overflow: hidden;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

        /*
          Remove old decorative circle.
          This was making the card look transparent/flat.
        */
        .stat-card::after {
          display: none !important;
        }

        .stat-card:hover {
          transform: translateY(-3px);

          border-color:
            rgba(var(--bs-primary-rgb), 0.28);

          box-shadow:
            0 9px 24px
            rgba(0, 0, 0, 0.10);
        }

        .stat-card-top {
          position: relative;

          display: flex;
          align-items: center;
          justify-content: space-between;

          margin-bottom: 18px;
        }

        .stat-icon {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 42px;
          height: 42px;

          border-radius: 12px;

          font-size: 20px;
        }

        .stat-icon-arrow {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 28px;
          height: 28px;

          border-radius: 50%;

          color: var(--bs-secondary-color);

          background:
            var(--dashboard-inner-bg);

          font-size: 14px;
        }

        .stat-percentage {
          padding: 5px 8px;

          border-radius: 6px;

          color: var(--bs-success);

          background:
            rgba(var(--bs-success-rgb), 0.11);

          font-size: 0.7rem;
          font-weight: 750;
        }

        .stat-label {
          margin-bottom: 3px;

          color: var(--bs-secondary-color);

          font-size: 0.72rem;
          font-weight: 650;
        }

        .stat-value {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 1.55rem;
          font-weight: 800;

          line-height: 1.25;
        }

        .stat-value small {
          color: var(--bs-secondary-color);

          font-size: 0.75rem;
          font-weight: 600;
        }

        .stat-value-currency {
          font-size: 1.35rem;
        }

        .stat-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 5px;

          margin-top: 10px;

          color: var(--bs-secondary-color);

          font-size: 0.66rem;
        }

        .stat-footer span {
          display: inline-flex;
          align-items: center;

          gap: 4px;
        }

        .stat-footer i {
          font-size: 12px;
        }


        /* =========================================================
           KPI ICON COLORS
        ========================================================= */

        .stat-card-primary .stat-icon {
          color: var(--bs-primary);

          background:
            rgba(var(--bs-primary-rgb), 0.11);
        }

        .stat-card-warning .stat-icon {
          color: var(--bs-warning);

          background:
            rgba(var(--bs-warning-rgb), 0.11);
        }

        .stat-card-success .stat-icon {
          color: var(--bs-success);

          background:
            rgba(var(--bs-success-rgb), 0.11);
        }

        .stat-card-info .stat-icon {
          color: var(--bs-info);

          background:
            rgba(var(--bs-info-rgb), 0.11);
        }


        /* =========================================================
           PROGRESS
        ========================================================= */

        .stat-progress {
          height: 5px;

          margin-top: 9px;

          border-radius: 20px;

          background:
            var(--bs-tertiary-bg);

          overflow: hidden;
        }

        .stat-progress span {
          display: block;

          height: 100%;

          border-radius: inherit;

          background: var(--bs-success);
        }


        /* =========================================================
           COMMON CARD
        ========================================================= */

        .dashboard-card {
  border: 1px solid var(--bs-border-color);
  border-radius: 16px;

  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.055);

  overflow: hidden;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

        .dashboard-card:hover {
          border-color:
            rgba(var(--bs-primary-rgb), 0.20);

          box-shadow:
            0 7px 20px
            rgba(0, 0, 0, 0.075);
        }


        /* =========================================================
           CARD HEADER
        ========================================================= */

        .card-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;

          gap: 15px;

          padding: 17px 18px;

          border-bottom:
            1px solid var(--bs-border-color);

        }

        .card-heading h6 {
          margin: 0 0 3px;

          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.84rem;
          font-weight: 750;
        }

        .card-heading p {
          margin: 0;

          color: var(--bs-secondary-color);

          font-size: 0.67rem;
        }

        .heading-icon {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 34px;
          height: 34px;

          flex-shrink: 0;

          border-radius: 9px;

          color: var(--bs-primary);

          background:
            rgba(var(--bs-primary-rgb), 0.10);

          font-size: 17px;
        }


        /* =========================================================
           FINANCIAL SUMMARY
        ========================================================= */

        .financial-summary-card {
          padding-bottom: 18px;
        }

        .financial-result {
          padding: 20px 18px 13px;
        }

        .result-label {
          display: block;

          margin-bottom: 4px;

          color: var(--bs-secondary-color);

          font-size: 0.68rem;
        }

        .result-value {
          font-size: 1.8rem;
          font-weight: 800;

          line-height: 1.2;
        }

        .result-status {
          display: inline-flex;
          align-items: center;

          gap: 4px;

          margin-top: 7px;

          font-size: 0.68rem;
          font-weight: 700;
        }

        .result-profit {
          color: var(--bs-success);
        }

        .result-loss {
          color: var(--bs-danger);
        }

        .result-neutral {
          color: var(--bs-secondary-color);
        }


        /* =========================================================
           FINANCE BREAKDOWN
        ========================================================= */

        .finance-breakdown {
          display: grid;

          grid-template-columns: 1fr 1fr;

          gap: 9px;

          padding: 0 18px;
        }

        .finance-item {
          display: flex;
          align-items: center;

          gap: 9px;

          padding: 10px;

          border: 1px solid var(--bs-border-color);

          border-radius: 10px;


          transition:
            border-color 0.2s ease,
            transform 0.2s ease;
        }

        .finance-item:hover {
          transform: translateY(-1px);

          border-color:
            rgba(var(--bs-primary-rgb), 0.20);
        }

        .finance-item-icon {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 30px;
          height: 30px;

          flex-shrink: 0;

          border-radius: 8px;

          font-size: 15px;
        }

        .finance-item-icon.collection {
          color: var(--bs-success);

          background:
            rgba(var(--bs-success-rgb), 0.11);
        }

        .finance-item-icon.expense {
          color: var(--bs-danger);

          background:
            rgba(var(--bs-danger-rgb), 0.11);
        }

        .finance-item span {
          display: block;

          color: var(--bs-secondary-color);

          font-size: 0.61rem;
        }

        .finance-item strong {
          display: block;

          margin-top: 2px;

          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.75rem;
        }


        /* =========================================================
           COLLECTION RATIO
        ========================================================= */

        .collection-ratio {
          padding: 16px 18px 0;
        }

        .ratio-header {
          display: flex;
          justify-content: space-between;

          margin-bottom: 6px;

          color: var(--bs-secondary-color);

          font-size: 0.62rem;
        }

        .ratio-header strong {
          color: var(--bs-body-color);
        }

        .ratio-track {
          height: 5px;

          border-radius: 20px;

          background:
            var(--bs-tertiary-bg);

          overflow: hidden;
        }

        .ratio-track span {
          display: block;

          height: 100%;

          border-radius: inherit;

          background: var(--bs-success);
        }


        /* =========================================================
           DONUT
        ========================================================= */

        .donut-section {
          display: flex;
          align-items: center;
          justify-content: center;

          gap: 20px;

          min-height: 250px;

          padding: 18px;
        }

        .donut-chart {
          position: relative;

          width: 145px;
          height: 145px;

          flex-shrink: 0;

          border-radius: 50%;

          display: flex;
          align-items: center;
          justify-content: center;

          transform: rotate(-90deg);
        }

        .donut-chart::before {
          content: "";

          position: absolute;

          inset: 13px;

          border-radius: 50%;

          
        }

        .donut-inner {
          position: relative;

          z-index: 2;

          display: flex;
          flex-direction: column;
          align-items: center;

          transform: rotate(90deg);
        }

        .donut-inner strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 1.15rem;
          font-weight: 800;
        }

        .donut-inner span {
          margin-top: 2px;

          color: var(--bs-secondary-color);

          font-size: 0.58rem;
        }


        /* =========================================================
           DONUT LEGEND
        ========================================================= */

        .donut-legend {
          flex: 1;
        }

        .donut-legend > div {
          display: grid;

          grid-template-columns:
            8px 1fr auto;

          align-items: center;

          gap: 7px;

          margin-bottom: 14px;
        }

        .donut-legend span:not(.legend-dot) {
          color: var(--bs-secondary-color);

          font-size: 0.63rem;
        }

        .donut-legend strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.68rem;
        }

        .legend-dot {
          display: inline-block;

          width: 7px;
          height: 7px;

          border-radius: 50%;
        }

        .collection-dot {
          background: var(--bs-success);
        }

        .expense-dot {
          background: var(--bs-danger);
        }


        /* =========================================================
           BAR CHART
        ========================================================= */

        .chart-legend {
          display: flex;
          align-items: center;

          gap: 12px;

          color: var(--bs-secondary-color);

          font-size: 0.58rem;
        }

        .chart-legend span {
          display: inline-flex;
          align-items: center;

          gap: 4px;
        }

        .bar-chart {
          display: flex;
          align-items: stretch;
          justify-content: space-between;

          height: 250px;

          padding: 25px 18px 15px;

          gap: 8px;

          
        }

        .chart-column {
          display: flex;
          flex-direction: column;
          align-items: center;

          flex: 1;

          min-width: 0;
        }

        .chart-bars {
          display: flex;
          align-items: flex-end;
          justify-content: center;

          gap: 4px;

          width: 100%;
          height: 205px;

          border-bottom:
            1px solid var(--bs-border-color);
        }

        .chart-bar {
          width: min(15px, 30%);

          min-height: 1px;

          border-radius: 5px 5px 0 0;

          transition:
            height 0.3s ease,
            opacity 0.2s ease;
        }

        .chart-bar:hover {
          opacity: 0.72;
        }

        .collection-bar {
          background: var(--bs-success);
        }

        .expense-bar {
          background: var(--bs-danger);
        }

        .chart-day {
          margin-top: 8px;

          color: var(--bs-secondary-color);

          font-size: 0.59rem;
          font-weight: 650;
        }


        /* =========================================================
           OCCUPANCY
        ========================================================= */

        .occupancy-layout {
          display: flex;
          align-items: center;

          gap: 35px;

          padding: 25px 22px;

          
        }

        .occupancy-circle {
          position: relative;

          width: 155px;
          height: 155px;

          flex-shrink: 0;
        }

        .occupancy-svg {
          width: 100%;
          height: 100%;

          transform: rotate(-90deg);
        }

        .occupancy-track {
          fill: none;

          stroke:
            var(--bs-tertiary-bg);

          stroke-width: 9;
        }

        .occupancy-progress {
          fill: none;

          stroke: var(--bs-success);

          stroke-width: 9;

          stroke-linecap: round;

          transition:
            stroke-dasharray 0.5s ease;
        }

        .occupancy-circle-content {
          position: absolute;

          inset: 0;

          display: flex;
          flex-direction: column;

          align-items: center;
          justify-content: center;
        }

        .occupancy-circle-content strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 1.45rem;
          font-weight: 800;
        }

        .occupancy-circle-content span {
          color: var(--bs-secondary-color);

          font-size: 0.61rem;
        }

        .occupancy-details {
          flex: 1;
        }

        .occupancy-detail {
          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 11px 0;

          border-bottom:
            1px solid var(--bs-border-color);
        }

        .occupancy-detail:last-child {
          border-bottom: 0;
        }

        .occupancy-detail span {
          display: flex;
          align-items: center;

          gap: 7px;

          color: var(--bs-secondary-color);

          font-size: 0.67rem;
        }

        .occupancy-detail i {
          color: var(--bs-primary);

          font-size: 15px;
        }

        .occupancy-detail strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.85rem;
        }


        /* =========================================================
           STUDENT OVERVIEW
        ========================================================= */

        .student-overview {
          padding: 20px;

          
        }

        .student-overview-total {
          display: flex;
          align-items: center;
          justify-content: space-between;

          padding-bottom: 17px;

          border-bottom:
            1px solid var(--bs-border-color);
        }

        .student-overview-total span {
          color: var(--bs-secondary-color);

          font-size: 0.7rem;
        }

        .student-overview-total strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 1.3rem;
          font-weight: 800;
        }

        .student-status-row {
          display: grid;

          grid-template-columns: 1fr 1fr;

          gap: 15px;

          margin-top: 17px;
        }

        .student-status {
          display: flex;
          align-items: center;

          gap: 9px;
        }

        .student-status-icon {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 34px;
          height: 34px;

          border-radius: 9px;

          font-size: 16px;
        }

        .student-status-icon.active {
          color: var(--bs-success);

          background:
            rgba(var(--bs-success-rgb), 0.11);
        }

        .student-status-icon.inactive {
          color: var(--bs-danger);

          background:
            rgba(var(--bs-danger-rgb), 0.11);
        }

        .student-status span {
          display: block;

          color: var(--bs-secondary-color);

          font-size: 0.6rem;
        }

        .student-status strong {
          display: block;

          margin-top: 2px;

          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.85rem;
        }

        .student-progress {
          margin-top: 20px;
        }

        .student-progress-bar {
          height: 7px;

          border-radius: 20px;

          background:
            var(--bs-tertiary-bg);

          overflow: hidden;
        }

        .student-progress-bar span {
          display: block;

          height: 100%;

          border-radius: inherit;

          background: var(--bs-success);
        }

        .student-progress-label {
          display: flex;
          justify-content: space-between;

          margin-top: 6px;

          color: var(--bs-secondary-color);

          font-size: 0.6rem;
        }


        /* =========================================================
           EMPTY STATE
        ========================================================= */

        .dashboard-empty {
          min-height: 220px;

          display: flex;
          flex-direction: column;

          align-items: center;
          justify-content: center;

          padding: 20px;

          text-align: center;
        }

        .dashboard-empty-icon {
          display: flex;
          align-items: center;
          justify-content: center;

          width: 50px;
          height: 50px;

          margin-bottom: 10px;

          border-radius: 14px;

          color: var(--bs-secondary-color);

          background:
            var(--bs-tertiary-bg);

          font-size: 22px;
        }

        .dashboard-empty strong {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.76rem;
        }

        .dashboard-empty span {
          max-width: 250px;

          margin-top: 3px;

          color: var(--bs-secondary-color);

          font-size: 0.62rem;
        }


        /* =========================================================
           CALENDAR
        ========================================================= */

        .calendar-wrapper {
          padding: 18px;

          
        }

        .calendar-wrapper .fc {
          color: var(--bs-body-color);

          font-size: 0.75rem;
        }

        .calendar-wrapper
        .fc-theme-standard td,
        .calendar-wrapper
        .fc-theme-standard th,
        .calendar-wrapper
        .fc-theme-standard
        .fc-scrollgrid {
          border-color:
            var(--bs-border-color);
        }

        .calendar-wrapper
        .fc-col-header-cell {
          padding: 8px 4px;

          background:
            var(--bs-tertiary-bg);
        }

        .calendar-wrapper
        .fc-col-header-cell-cushion {
          color: var(--bs-secondary-color);

          font-size: 0.65rem;
          font-weight: 750;

          text-decoration: none;
        }

       

        .calendar-wrapper
        .fc-daygrid-day-number {
          padding: 7px;

          color: var(--bs-body-color);

          font-size: 0.68rem;
          font-weight: 650;

          text-decoration: none;
        }

        .calendar-wrapper
        .fc-day-today {
          background:
            rgba(var(--bs-primary-rgb), 0.07)
            !important;
        }

        .calendar-wrapper
        .fc-day-today
        .fc-daygrid-day-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 25px;
          height: 25px;

          margin: 3px;

          padding: 0;

          border-radius: 50%;

          color:
            var(--bs-white);

          background:
            var(--bs-primary);
        }

        .calendar-wrapper
        .fc-toolbar {
          margin-bottom: 15px !important;
        }

        .calendar-wrapper
        .fc-toolbar-title {
          color:
            var(
              --bs-heading-color,
              var(--bs-body-color)
            );

          font-size: 0.95rem;
          font-weight: 750;
        }

        .calendar-wrapper
        .fc-button {
          border:
            1px solid var(--bs-border-color);

          background:
            var(--dashboard-inner-bg);

          color:
            var(--bs-body-color);

          box-shadow: none !important;

          font-size: 0.67rem;
        }

        .calendar-wrapper
        .fc-button:hover {
          border-color:
            rgba(var(--bs-primary-rgb), 0.4);

          background:
            rgba(var(--bs-primary-rgb), 0.08);

          color: var(--bs-primary);
        }

        .calendar-wrapper
        .fc-button-active {
          border-color:
            var(--bs-primary) !important;

          background:
            rgba(var(--bs-primary-rgb), 0.10)
            !important;

          color:
            var(--bs-primary) !important;
        }

        .calendar-wrapper
        .fc-event {
          border: 0;

          border-radius: 5px;

          padding: 2px 4px;

          font-size: 0.61rem;
        }


        /* =========================================================
           RESPONSIVE
        ========================================================= */

        @media (max-width: 1199px) {

          .donut-section {
            min-height: 220px;
          }

          .occupancy-layout {
            gap: 20px;
          }
        }


        @media (max-width: 767px) {

          .dashboard-topbar {
            align-items: flex-start;

            flex-direction: column;

            padding: 18px;
          }

          .dashboard-status {
            width: 100%;

            justify-content: center;
          }

          .stat-card {
            min-height: 155px;
          }

          .donut-section {
            justify-content: space-around;
          }

          .occupancy-layout {
            flex-direction: column;
          }

          .occupancy-details {
            width: 100%;
          }

          .student-status-row {
            grid-template-columns: 1fr;
          }

          .chart-legend {
            display: none;
          }

          .calendar-wrapper {
            overflow-x: auto;
          }

          .calendar-wrapper .fc {
            min-width: 650px;
          }

          .calendar-wrapper .fc-toolbar {
            flex-wrap: wrap;

            gap: 8px;
          }
        }


        @media (max-width: 450px) {

          .donut-section {
            flex-direction: column;
          }

          .donut-legend {
            width: 100%;
          }

          .finance-breakdown {
            grid-template-columns: 1fr;
          }
        }

      `}</style>
    </>
  );
}

/* ===============================================================
   EMPTY STATE
================================================================ */

function EmptyState({ icon, title, text }) {
  return (
    <div className="dashboard-empty">
      <div className="dashboard-empty-icon">
        <i className={icon}></i>
      </div>

      <strong>{title}</strong>

      <span>{text}</span>
    </div>
  );
}
